import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { themes } from "../utils/Themes";

const ThemeContext = createContext();

const THEME_KEY = "portfolio_theme";
const FONT_KEY = "portfolio_font";

// ═══════════════════════════════════════════════════════════════════
// DEFAULT CONFIGURATION
// To change the default theme or font, edit the values below.
// DEFAULT_THEME options: "dark", "light", "green", "orange", "cyan", "rose"
// DEFAULT_FONT options:  "default", "inter", "jetbrains", "outfit",
//   "dancing", "pacifico", "caveat", "bangers", "fredoka",
//   "space", "nunito", "bricolage"
// ═══════════════════════════════════════════════════════════════════
const DEFAULT_THEME = "dark";
const DEFAULT_FONT = "default";

const fontOptions = {
  // Standard / Professional
  default: '"Poppins", sans-serif',
  inter: '"Inter", sans-serif',
  jetbrains: '"JetBrains Mono", monospace',
  outfit: '"Outfit", sans-serif',
  // Cursive / Handwriting
  dancing: '"Dancing Script", cursive',
  pacifico: '"Pacifico", cursive',
  caveat: '"Caveat", cursive',
  // Cartoon / Playful
  bangers: '"Bangers", cursive',
  fredoka: '"Fredoka", sans-serif',
  // Modern / Trending
  space: '"Space Grotesk", sans-serif',
  nunito: '"Nunito", sans-serif',
  bricolage: '"Bricolage Grotesque", sans-serif',
};

// Google Fonts URL fragments for each font key (default/Poppins is preloaded in HTML)
const fontUrls = {
  inter: "Inter:wght@400;500;600;700",
  jetbrains: "JetBrains+Mono:wght@400;500;600;700",
  outfit: "Outfit:wght@400;500;600;700",
  dancing: "Dancing+Script:wght@400;500;600;700",
  pacifico: "Pacifico",
  caveat: "Caveat:wght@400;500;600;700",
  bangers: "Bangers",
  fredoka: "Fredoka:wght@400;500;600;700",
  space: "Space+Grotesk:wght@400;500;600;700",
  nunito: "Nunito:wght@400;500;600;700",
  bricolage: "Bricolage+Grotesque:wght@400;500;600;700",
};

// Dynamically inject a Google Fonts <link> tag for a given font key.
// Skips if already loaded or if it's the default (Poppins, preloaded in HTML).
function loadFontStylesheet(fontKey, loadedRef) {
  if (fontKey === "default" || !fontUrls[fontKey] || loadedRef.current.has(fontKey)) return;
  loadedRef.current.add(fontKey);

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${fontUrls[fontKey]}&display=swap`;
  document.head.appendChild(link);
}

export const ThemeContextProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    try {
      return localStorage.getItem(THEME_KEY) || DEFAULT_THEME;
    } catch {
      return DEFAULT_THEME;
    }
  });

  const [fontMode, setFontMode] = useState(() => {
    try {
      return localStorage.getItem(FONT_KEY) || DEFAULT_FONT;
    } catch {
      return DEFAULT_FONT;
    }
  });

  // Track which fonts have already been loaded to avoid duplicate <link> tags
  const loadedFonts = useRef(new Set(["default"]));

  // Load the saved font on initial mount (if not the default)
  useEffect(() => {
    loadFontStylesheet(fontMode, loadedFonts);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentTheme = themes[themeMode] || themes[DEFAULT_THEME];
  const currentFont = fontOptions[fontMode] || fontOptions[DEFAULT_FONT];

  const changeTheme = useCallback((mode) => {
    if (themes[mode]) {
      setThemeMode(mode);
      try {
        localStorage.setItem(THEME_KEY, mode);
      } catch {}
    }
  }, []);

  const changeFont = useCallback((font) => {
    if (fontOptions[font]) {
      loadFontStylesheet(font, loadedFonts);
      setFontMode(font);
      try {
        localStorage.setItem(FONT_KEY, font);
      } catch {}
    }
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        themeMode,
        fontMode,
        currentTheme,
        currentFont,
        changeTheme,
        changeFont,
        fontOptions,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeContext must be used within ThemeContextProvider");
  }
  return context;
};
